/**
 * Decords on-demand custom select loader.
 * The original theme component is stored in custom-select-core.js.
 */
(() => {
  'use strict';

  if (window.DecordsCustomSelectLoader) return;

  const componentSelector = 'custom-select, country-selector';
  const source = document.currentScript || Array.from(document.scripts).reverse().find((script) =>
    /\/custom-select\.js(?:\?|$)/.test(script.src)
  );

  if (!source || !source.src) return;

  const sourceUrl = new URL(source.src, document.baseURI);
  const coreUrl = new URL(source.dataset.coreSrc || sourceUrl.href);
  if (!source.dataset.coreSrc) {
    coreUrl.pathname = coreUrl.pathname.replace(/\/custom-select\.js$/, '/custom-select-core.js');
  }

  let request;
  const observed = new WeakSet();
  const interactionPending = new WeakSet();

  const isReady = () => Boolean(customElements.get('custom-select'));
  const report = (error) => console.error('[Decords custom select]', error);

  const ensure = () => {
    if (isReady()) return Promise.resolve(customElements.get('custom-select'));
    if (request) return request;

    request = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = coreUrl.href;
      script.async = false;
      script.dataset.decordsCustomSelectCore = '';
      script.onload = () => {
        if (isReady()) {
          resolve(customElements.get('custom-select'));
        } else {
          reject(new Error('custom-select-core.js loaded without defining custom-select'));
        }
      };
      script.onerror = () => reject(new Error(`Unable to load ${script.src}`));
      document.head.appendChild(script);
    }).catch((error) => {
      request = null;
      throw error;
    });

    return request;
  };

  const replayAfterUpgrade = (component, control, createEvent) => {
    if (interactionPending.has(component)) return;
    interactionPending.add(component);
    control.classList.add('is-loading');
    control.setAttribute('aria-busy', 'true');

    ensure()
      .then(() => {
        const replay = () => {
          interactionPending.delete(component);
          control.classList.remove('is-loading');
          control.removeAttribute('aria-busy');

          if (!component.isConnected || !control.isConnected) return;
          control.dispatchEvent(createEvent());
        };

        // The original component initialises through IntersectionObserver.
        // Two animation frames allow the upgraded, visible element to bind its listeners.
        requestAnimationFrame(() => requestAnimationFrame(replay));
      })
      .catch((error) => {
        interactionPending.delete(component);
        control.classList.remove('is-loading');
        control.removeAttribute('aria-busy');
        report(error);
      });
  };

  const proximityObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        ensure().catch(report);
      });
    }, { rootMargin: '600px 0px 600px 0px' })
    : null;

  const watch = (component) => {
    if (!component || isReady() || observed.has(component)) return;
    observed.add(component);

    if (proximityObserver) {
      proximityObserver.observe(component);
    } else {
      ensure().catch(report);
    }
  };

  const scan = (root = document) => {
    if (!root) return;

    if (root.nodeType === Node.ELEMENT_NODE && root.matches(componentSelector)) {
      watch(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(componentSelector).forEach(watch);
    }
  };

  document.addEventListener('mousedown', (event) => {
    if (isReady() || !(event.target instanceof Element)) return;

    const control = event.target.closest('.custom-select__btn');
    const component = control && control.closest(componentSelector);
    if (!control || !component || event.button !== 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    replayAfterUpgrade(
      component,
      control,
      () => new MouseEvent('mousedown', {
        bubbles: true,
        cancelable: true,
        button: 0,
        buttons: 1,
        view: window
      })
    );
  }, true);

  document.addEventListener('keydown', (event) => {
    if (isReady() || !(event.target instanceof Element)) return;
    if (!['ArrowUp', 'ArrowDown', ' '].includes(event.key)) return;

    const component = event.target.closest(componentSelector);
    if (!component) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    replayAfterUpgrade(
      component,
      event.target,
      () => new KeyboardEvent('keydown', {
        key: event.key,
        code: event.code,
        bubbles: true,
        cancelable: true,
        repeat: false
      })
    );
  }, true);

  const start = () => {
    scan(document);

    if ('MutationObserver' in window) {
      new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) scan(node);
          });
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
  };

  window.DecordsCustomSelectLoader = Object.freeze({
    ensure,
    scan,
    isReady
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
